import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as EmailValidator from 'email-validator';
import AddInput from '../../components/AddInput';
import BasicInput from '../../components/BasicInput';
import BasicOption from '../../components/BasicOption';
import ButtonTextIcon, { BtnType } from '../../components/BtnIcon';
import { ModalActionType, ModalProps } from '../../components/Modal';
import api from '../../config/axios';
import HolderProps from '../../dto/holder';
import rgEmittent from '../../dto/rgEmittent';
import { statesOfBrazil } from '../../dto/states';
import CEPFinder from '../../helpers/findAddressByCep';
import {
  maskCep, maskCPForCNPJ, maskDate, maskNumber, maskPhone, unmaskData,
} from '../../helpers/inputMasks.js';
import showApiErrors from '../../helpers/showApiErrors';
import { validateCnpjOrCpf, validateCpf } from '../../helpers/validate';
import DefaultLayout from '../../Layout';
import TextAreaInput from '../../components/TextAreaInput';

export default function AddNewDependentPF() {
  const navigate = useNavigate();
  const { holderId } = useParams();
  const [loading, setLoading] = useState(false);
  const [showToastSuccess, setShowToastSuccess] = useState(false);
  const [showToastError, setShowToastError] = useState(false);
  const [modal, setModal] = useState<ModalProps | null>(null);
  const [holder, setHolder] = useState<HolderProps>();
  const [token] = useState({ headers: { Authorization: `bearer ${localStorage.getItem('adminToken')}` } });
  const [dependent, setDependent] = useState({
    cpf: '',
    name: '',
    cardname: '',
    sex: 'm',
    birthday: '',
    rg: '',
    showCpfOnCard: true,
    emitent: 'SSP/MA',
    addressStreet: '',
    email: '',
    addressIbge: '',
    instagram: '',
    addressNumber: '',
    addressComplement: '',
    addressNeighborhood: '',
    addressCity: '',
    addressState: '',
    addressZipcode: '',
    observation: '',
    isActive: true,
    phone: '',
    phone2: '',
    enableTelemedicine: false,
    role: 'Cônjuge',
  });

  async function changeAddressByCep() {
    try {
      setLoading(true);
      const findCep = await new CEPFinder(unmaskData(dependent.addressZipcode)).findCep();
      if (findCep !== null) {
        setDependent({
          ...dependent,
          addressCity: findCep.city,
          addressState: findCep.state,
          addressNeighborhood: findCep.neighborhood,
          addressStreet: findCep.street,
          addressIbge: findCep.ibge,
        });
      }
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
    }
  }

  function enabledButton() {
    if (dependent.name.length > 0
        && validateCpf(dependent.cpf)
        && dependent.addressStreet.length > 0
        && dependent.addressNumber.length > 0
        && dependent.addressNeighborhood.length > 0
        && dependent.addressCity.length > 0
        && dependent.addressCity.length > 0
        && dependent.addressZipcode.length >= 10
        && dependent.phone.length > 2
    ) {
      return true;
    }
    return false;
  }

  function resolveRole() {
    if (dependent.role === 'Cônjuge') {
      return 'spouse';
    }
    if (dependent.role === 'Filho(a)') {
      return 'son';
    }
    return 'other';
  }

  async function SaveDependent() {
    try {
      setLoading(true);
      await api.post(`admin/dependent/${holderId}`, {
        ...dependent,
        role: resolveRole(),
        cpf: unmaskData(dependent.cpf),
        addressZipcode: unmaskData(dependent.addressZipcode),
        phone: unmaskData(dependent.phone),
        phone2: dependent.phone2 && dependent.phone2.length > 0 ? unmaskData(dependent.phone2) : '',
        observation: dependent.observation || '',
      }, token);
      setLoading(false);
      navigate(-1);
    } catch (err: any) {
      if (typeof err.response !== 'undefined') {
        if (err.response.status === 422) {
          setModal(showApiErrors(setModal, err.response.data.errors));
        }
      } else {
        setModal({ title: 'Ocorreu um erro ao cadastrar', children: 'Não foi possível cadastrar um dependente. Caso o problema persista, favor entrar em contato com suporte.', actions: [{ label: 'Ok, entendi', onClick: () => setModal(null), type: ModalActionType.PRIMARY }] });
      }
      console.log(err.response);
      setLoading(false);
    }
  }

  async function fetchHolder() {
    try {
      setLoading(true);
      const response = await api.get(`/admin/holder/${holderId}`, token);
      console.log(response);
      setHolder(response.data);
      setLoading(false);
    } catch (err) {
      setModal({ title: 'Ocorreu um erro', children: 'Não foi possível localizar o titular desse dependente. Verifique a URL e conectivdade e tente novamente em instantes.', actions: [{ label: 'Ok, entendi', onClick: () => navigate(-1), type: ModalActionType.PRIMARY }] });
    }
  }

  useEffect(() => {
    fetchHolder();
  }, []);

  return (
    <DefaultLayout
      pageTitle={holder ? `${holder.name} > Adicionar dependente` : 'Titular > Adicionar dependente'}
      loading={loading}
      showToastSuccess={showToastSuccess}
      showToastError={showToastError}
      showRightSlider={false}
      rightSliderContent={undefined}
      modal={modal}
      setCloseRightSlider={() => null}
    >
      <div className="row holders card-bg p-0">
        <section className="row">
          <h1>Cadastro dependente</h1>

          <div className="col-12 mb-4">
            <ButtonTextIcon
              label="Preencher endereço do titular"
              type={BtnType.PRIMARY}
              icon=""
              active={!!holder}
              onClick={() => setDependent({
                ...dependent,
                addressZipcode: maskCep(holder!.addressZipcode),
                addressStreet: holder!.addressStreet,
                addressNumber: holder!.addressNumber,
                addressComplement: holder!.addressComplement,
                addressNeighborhood: holder!.addressNeighborhood,
                addressCity: holder!.addressCity,
                addressState: holder!.addressState,
              })}
              ref={null}
            />
          </div>
          <div className="col-4">
            <BasicOption
              id="name-input"
              label="Relação com titular"
              readOnly={false}
              helper="Ex: Filho(a)"
              error=""
              value={dependent.role}
              onChange={(data) => setDependent({ ...dependent, role: data })}
            >
              {['Cônjuge', 'Filho(a)', 'Outro']}
            </BasicOption>
          </div>
          <div className="col-4">
            <BasicInput
              id="name-input"
              label="Nome"
              readOnly={false}
              helper="Ex: João Batista"
              error={dependent.name.length < 1 ? 'Campo Obrigatório' : ''}
              value={dependent.name}
              type="text"
              onChange={(data) => setDependent({ ...dependent, name: data })}
            />
          </div>
          <div className="col-4">
            <BasicInput
              id="card-name-input"
              label="Nome no cartão"
              readOnly={false}
              helper="Ex: João B. C. Vieira"
              error=""
              value={dependent.cardname === null ? '' : dependent.cardname}
              type="text"
              onChange={(data) => setDependent({ ...dependent, cardname: data })}
            />
          </div>
          <div className="col-4">
            <BasicInput
              id="cpf-input"
              label="CPF"
              readOnly={false}
              helper="Ex: 000.000.000-10"
              error={!validateCnpjOrCpf(dependent.cpf) ? 'Digite um CPF válido.' : ''}
              value={dependent.cpf}
              type="text"
              onChange={(data) => setDependent({ ...dependent, cpf: maskCPForCNPJ(data) })}
            />
          </div>
          <div className="col-4">
            <BasicInput
              id="rg-input"
              label="RG"
              readOnly={false}
              helper="Ex: 99999999"
              error=""
              value={dependent.rg}
              type="text"
              onChange={(data) => setDependent({ ...dependent, rg: maskNumber(data) })}
            />
          </div>
          <div className="col-4">
            <BasicOption
              id="rg-emittent-input"
              label="RG / Emissor"
              readOnly={false}
              helper="Ex: SSP/MA"
              error=""
              value={dependent.emitent}
              onChange={(data) => setDependent({ ...dependent, emitent: data })}
            >
              {[...rgEmittent]}
            </BasicOption>
          </div>
          <div className="col-4">
            <BasicOption
              id="sex-input"
              label="Sexo"
              readOnly={false}
              helper="Ex: Masculino / Feminino"
              error=""
              value={dependent.sex === 'm' ? 'Masculino' : 'Feminino'}
              onChange={(data) => setDependent({ ...dependent, sex: data === 'Masculino' ? 'm' : 'f' })}
            >
              {['Masculino', 'Feminino']}
            </BasicOption>
          </div>
          <div className="col-4">
            <BasicInput
              id="birth-input"
              label="DT. Nascimento"
              readOnly={false}
              helper="Ex: 13-01-1990"
              error={dependent.birthday.length < 1 ? 'Campo obrigatório' : ''}
              value={dependent.birthday}
              type="text"
              onChange={(data) => setDependent({ ...dependent, birthday: maskDate(data) })}
            />
          </div>
          <div className="col-4">
            <AddInput
              id="address-cep-input"
              label="CEP"
              readOnly={false}
              helper="Ex: 35.171-120"
              error={dependent.addressZipcode.length < 10 ? 'Campo obrigatório' : ''}
              value={dependent.addressZipcode}
              type="text"
              onAdd={() => changeAddressByCep()}
              onChange={(data) => setDependent({ ...dependent, addressZipcode: maskCep(data) })}
            />
          </div>
          <div className="col-4">
            <BasicInput
              id="address-street-input"
              label="Rua"
              readOnly={false}
              helper="Ex: Rua Três"
              error={dependent.addressStreet.length < 2 ? 'Campo Obrigatório' : ''}
              value={dependent.addressStreet}
              type="text"
              onChange={(data) => setDependent({ ...dependent, addressStreet: data })}
            />
          </div>
          <div className="col-4">
            <BasicInput
              id="address-number-input"
              label="Número"
              readOnly={false}
              helper="Ex: 133"
              error={dependent.addressNumber.length < 1 ? 'Campo Obrigatório' : ''}
              value={dependent.addressNumber}
              type="text"
              onChange={(data) => setDependent({ ...dependent, addressNumber: data })}
            />
          </div>
          <div className="col-4">
            <BasicInput
              id="address-complement-input"
              label="Complemento"
              readOnly={false}
              helper="Ex: Bloco A"
              error=""
              value={dependent.addressComplement}
              type="text"
              onChange={(data) => setDependent({ ...dependent, addressComplement: data })}
            />
          </div>
          <div className="col-4">
            <BasicInput
              id="address-neighborhood-input"
              label="Bairro"
              readOnly={false}
              helper="Centro"
              error={dependent.addressNeighborhood.length < 1 ? 'Campo obrigatório' : ''}
              value={dependent.addressNeighborhood}
              type="text"
              onChange={(data) => setDependent({ ...dependent, addressNeighborhood: data })}
            />
          </div>
          <div className="col-4">
            <BasicInput
              id="address-city-input"
              label="Cidade"
              readOnly
              helper="Ex: Imperatriz"
              error={dependent.addressCity.length < 1 ? 'Campo obrigatório' : ''}
              value={dependent.addressCity}
              type="text"
              onChange={(data) => setDependent({ ...dependent, addressCity: data })}
            />
          </div>
          <div className="col-4">
            <BasicOption
              id="address-state-input"
              label="Estado"
              readOnly
              helper=""
              error=""
              value={dependent.addressState}
              onChange={(data) => setDependent({ ...dependent, addressState: data })}
            >
              {statesOfBrazil}
            </BasicOption>
          </div>
          <div className="col-4">
            <BasicInput
              id="phone-input"
              label="Telefone"
              readOnly={false}
              helper="Ex: (31) 99999 - 9999"
              error={dependent.phone.length < 1 ? 'Campo obrigatório' : ''}
              value={dependent.phone}
              type="text"
              onChange={(data) => setDependent({ ...dependent, phone: maskPhone(data) })}
            />
          </div>
          <div className="col-4">
            <BasicInput
              id="phone2-input"
              label="Telefone 2"
              readOnly={false}
              helper="Ex: (31) 99999 - 9999"
              error=""
              value={dependent.phone2 || ''}
              type="text"
              onChange={(data) => setDependent({ ...dependent, phone2: maskPhone(data) })}
            />
          </div>
          <div className="col-4">
            <BasicInput
              id="email-input"
              label="email"
              readOnly={false}
              helper="Ex: usuario@usuario.com"
              error={dependent.email && dependent.email.length > 0 && !EmailValidator.validate(dependent.email) ? 'E-mail inválido' : ''}
              value={dependent.email ? dependent.email : ''}
              type="text"
              onChange={(data) => setDependent({ ...dependent, email: data })}
            />
          </div>
          <div className="col-4">
            <BasicInput
              id="instagram-input"
              label="instagram"
              readOnly={false}
              helper="Ex: valdecir_roberto"
              error=""
              value={dependent.instagram ? dependent.instagram : ''}
              type="text"
              onChange={(data) => setDependent({ ...dependent, instagram: data })}
            />
          </div>
          <div className="col-12">
            <TextAreaInput
              id=""
              label="Observação"
              readOnly={false}
              helper=""
              error=""
              value={dependent.observation || ''}
              onChange={(data) => setDependent({ ...dependent, observation: data })}
            />
          </div>
          <div className="col-12 optionals mt-4 mb-4">
            <p>Opcionais</p>
            <div className="check-wrapper">
              <input type="checkbox" checked={!dependent.showCpfOnCard} onChange={() => setDependent({ ...dependent, showCpfOnCard: !dependent.showCpfOnCard })} />
              <p>Não mostrar CPF no cartão</p>
            </div>
            <div className="check-wrapper">
              <input type="checkbox" checked={dependent.enableTelemedicine} onChange={() => setDependent({ ...dependent, enableTelemedicine: !dependent.enableTelemedicine })} />
              <p>Telemedicina</p>
            </div>
          </div>
          <div className="col-12 footer">
            <ButtonTextIcon
              label="salvar"
              type={BtnType.PRIMARY}
              icon="backup"
              active={enabledButton()}
              onClick={() => SaveDependent()}
              ref={null}
            />
          </div>
        </section>
      </div>
    </DefaultLayout>
  );
}
